// Guías de Aprender · Estadísticas, comunidad, torneos, puzzles y bots (2026-10-18).

export const COMUNIDAD_GUIDES = [
  {
    slug: "estadisticas",
    theme: "estadisticas",
    nav: "Tus estadísticas",
    title: "Estadísticas de XIMA: victorias, rachas y CIMAX",
    description:
      "Qué muestran tus estadísticas en XIMA: partidas, victorias, porcentaje de victorias, rachas, torneos ganados y CIMAX por modo, contando todas tus partidas.",
    h1: "Tus estadísticas",
    intro:
      "Estadísticas reúne en una sola página cómo te va en XIMA. Cuenta todas las partidas que has jugado con tu cuenta y puedes verlas juntas o modo por modo.",
    sections: [
      {
        h: "Qué encontrarás",
        list: [
          "Partidas jugadas, victorias y derrotas.",
          "Tu porcentaje de victorias.",
          "Tus rachas de victorias.",
          "Los torneos que has ganado.",
          "Tu CIMAX en cada modo de juego.",
          "Datos como el tiempo que juegas o los bloques que capturas.",
        ],
      },
      {
        h: "Filtrar por modo",
        p: [
          "Arriba puedes elegir Todos o un modo concreto: Ciencia, Azar, Cienzar o Ciencia V/S Azar (Contrazar). Al pulsar una pestaña, una fila de la tabla o una fila de CIMAX, toda la página se filtra por ese modo; si vuelves a pulsar, regresas a Todos.",
        ],
      },
      {
        h: "Cómo usarlas para mejorar",
        list: [
          "Compara tus resultados entre modos: quizá juegas mucho mejor sin dados que con ellos.",
          "Si tu porcentaje de victorias baja, abre el Historial y analiza tus últimas derrotas.",
          "Mira tu CIMAX con el tiempo: es la medida más fiable de tu nivel.",
        ],
      },
    ],
    faqs: [
      ["¿Las estadísticas cuentan todas mis partidas?", "Sí, todas las que has jugado con tu cuenta."],
      ["¿Otros jugadores ven mis estadísticas?", "Tu historial y tus repeticiones son públicos, y tu CIMAX aparece en el ranking."],
    ],
    related: ["estadisticas/cimax", "ranking", "historial-y-replay", "perfil"],
    cta: { label: "Qué es el CIMAX", slug: "estadisticas/cimax" },
    legacy: [
      "estadisticas/torneos-ganados-por-modo",
      "estadisticas/tiempo-jugado",
      "estadisticas/win-rate",
      "estadisticas/racha-victorias",
      "estadisticas/bloques-capturados",
      "estadisticas/leer-progreso",
      "estadisticas/comparar-modos",
    ],
  },
  {
    slug: "estadisticas/cimax",
    theme: "estadisticas",
    nav: "El CIMAX",
    title: "CIMAX: el sistema de puntuación de XIMA",
    description:
      "Qué es el CIMAX de XIMA y cómo se calcula: empiezas con 1000 puntos y ganas o pierdes según la fuerza, la potencia, el tiempo y los turnos de cada partida.",
    h1: "El CIMAX",
    intro:
      "El CIMAX es la puntuación que mide tu nivel en XIMA. Se parece al Elo del ajedrez, pero no mira solo si ganas o pierdes: también tiene en cuenta cómo jugaste.",
    sections: [
      {
        h: "Lo básico",
        list: [
          "Todo jugador empieza con 1000 puntos de CIMAX.",
          "Hay un CIMAX distinto para cada modo de juego.",
          "Lo que gana el ganador de una partida se le resta al perdedor.",
          "Cuanto mejor juegues (más fuerte, más rápido y en menos turnos), más se mueve.",
        ],
      },
      {
        h: "Por qué es distinto",
        p: [
          "Según el libro «Cómo llegar a la Xima», el CIMAX es un método matemático para medir el nivel y la habilidad relativa de cada jugador. No se basa en un resultado esperado por la diferencia de nivel, sino en lo que pasó realmente en la partida: la fuerza de los movimientos, la potencia, el nivel del juego, el tiempo usado frente al tiempo pactado y los turnos empleados.",
        ],
      },
      {
        h: "Los ingredientes",
        defs: [
          ["Fuerza de juego (FJ)", "La suma del valor de tus bloques en el tablero, de los que tienes fuera, de los que capturaste, más un extra si ganaste o si el rival se rindió."],
          ["Nivel de juego (NJ)", "La suma de las fuerzas de juego de los dos jugadores. Una partida reñida entre dos jugadores fuertes tiene un nivel alto."],
          ["Tiempo efectivo (te)", "El tiempo que usaste dividido entre el tiempo pactado."],
          ["Turnos (T)", "Los turnos que necesitaste."],
          ["Potencia de juego (PJ)", "Tu fuerza dividida entre 2 elevado al tiempo efectivo y multiplicada por los turnos: PJ = FJ ÷ (2^te × T). Jugar fuerte, rápido y en pocos turnos da más potencia."],
        ],
      },
      {
        h: "La fórmula",
        p: [
          "El libro resume el cálculo así: el CIMAX que se transfiere es igual al nivel de juego, multiplicado por (la mayor potencia + 1) dividida entre (la menor potencia + 1), y multiplicado por el CIMAX actual del perdedor dividido entre el CIMAX del ganador.",
          "CIMAX = NJ × (Pmáx + 1) ÷ (Pmín + 1) × (CIMAX del perdedor ÷ CIMAX del ganador)",
          "La última parte hace que ganar a alguien con más CIMAX que tú te dé más puntos, y que ganar a alguien con mucho menos te dé menos.",
        ],
      },
      {
        h: "Algunos valores de la fuerza de juego",
        list: [
          "Valor por ganar: llegar a la cima con el especial suma 16 puntos de fuerza.",
          "Valor por rendición: el que se rinde (o se queda sin tiempo) entrega al rival la mitad de su fuerza de juego.",
          "Bloques capturados: al capturar un bloque te quedas con el valor que tenía en ese momento.",
          "Bloques fuera: cada bloque que aún tienes fuera vale lo mismo que el promedio de tus bloques en el tablero.",
        ],
        after: ["Para saber cuánto vale cada bloque según su piso, mira la guía Valor de los bloques."],
      },
      {
        h: "No te obsesiones",
        p: [
          "El propio libro lo dice: una derrota no define el nivel de nadie. Juega cada partida hasta el final, porque el CIMAX tiene en cuenta cómo jugaste, y lo que importa es la tendencia, no un resultado aislado.",
        ],
      },
    ],
    faqs: [
      ["¿Con cuánto CIMAX empiezo?", "Con 1000 puntos en cada modo."],
      ["¿Se pierde CIMAX al perder?", "Sí. Lo que gana el ganador se le resta al perdedor."],
      ["¿Es lo mismo que el Elo?", "Se parece en que sube y baja con los resultados, pero el CIMAX también mide la fuerza, el tiempo y los turnos de cada partida."],
    ],
    related: ["estrategia/valor-de-los-bloques", "ranking", "estadisticas"],
    cta: { label: "Ver el ranking", slug: "ranking" },
    legacy: [
      "estadisticas/cimax-ciencia",
      "estadisticas/cimax-cienzar",
      "estadisticas/cimax-contrazar",
      "estadisticas/cimax-azar",
      "estadisticas/interpretar-cimax",
      "glosario/cimax",
      "cimax/segun-el-libro",
      "cimax/fuerza-nivel-potencia",
    ],
    en: "en/cimax",
  },
  {
    slug: "ranking",
    theme: "estadisticas",
    nav: "El ranking",
    title: "Ranking de XIMA: los mejores jugadores por modo",
    description:
      "El ranking de XIMA ordena a los jugadores por su CIMAX en cada modo (Ciencia, Azar, Cienzar y Contrazar) y en general. Cómo subir y cómo ver a los mejores.",
    h1: "El ranking",
    intro:
      "El ranking muestra quiénes son los mejores jugadores de XIMA según su CIMAX. Hay una clasificación para cada modo y una general.",
    sections: [
      {
        h: "Clasificaciones",
        list: [
          "Por modo: Ciencia, Azar, Cienzar y Contrazar (Ciencia V/S Azar).",
          "General: combina los datos de todos los modos.",
        ],
        after: ["También verás qué jugadores están conectados en ese momento."],
      },
      {
        h: "Cómo subir",
        list: [
          "Gana partidas, sobre todo contra rivales con más CIMAX que tú.",
          "Juega bien hasta el final: el CIMAX tiene en cuenta tu fuerza, tu tiempo y tus turnos.",
          "Céntrate en un modo: cada uno tiene su propia clasificación.",
        ],
      },
      {
        h: "Aprende de los mejores",
        p: [
          "Pulsa el nombre de cualquier jugador del ranking para abrir su historial y ver las repeticiones de sus partidas.",
        ],
      },
      {
        h: "Si no te ves en el ranking",
        p: [
          "El ranking se actualiza solo. Si acabas de jugar y aún no ves el cambio, recarga la página. Recuerda que las partidas como invitado no cuentan hasta que te registras.",
        ],
      },
    ],
    faqs: [
      ["¿Cómo se ordena el ranking?", "Por CIMAX, en cada modo y en general."],
      ["¿Puedo ver las partidas de los primeros del ranking?", "Sí. Pulsa su nombre para abrir su historial."],
    ],
    related: ["estadisticas/cimax", "estadisticas", "historial-y-replay", "torneos"],
    cta: { label: "Qué es el CIMAX", slug: "estadisticas/cimax" },
    legacy: [
      "ranking/global",
      "ranking/por-modo",
      "ranking/ciencia",
      "ranking/cienzar",
      "ranking/contrazar",
      "ranking/azar",
      "ranking/jugadores-en-linea",
      "ranking/leer-tabla",
      "ranking/competir-por-modo",
      "solucion/ranking-no-actualiza",
    ],
  },
  {
    slug: "perfil",
    theme: "comunidad",
    nav: "Tu perfil y Modo Diseñar",
    title: "Tu perfil en XIMA: código de jugador y Modo Diseñar",
    description:
      "Qué hay en tu perfil de XIMA: tu código de jugador, avatar y datos, y cómo personalizar bloques, tablero y paredes en Modo Diseñar. Todo se guarda solo.",
    h1: "Tu perfil y Modo Diseñar",
    intro:
      "Tu perfil es tu carta de presentación en XIMA, y Modo Diseñar es donde dejas el juego a tu gusto.",
    sections: [
      {
        h: "Tu código de jugador",
        p: [
          "Cada cuenta tiene un código de jugador (con la forma XIMA-…). Es lo que tus amigos necesitan para encontrarte en Amigos. Compártelo sin miedo: no da acceso a tu cuenta.",
        ],
      },
      {
        h: "Tus datos",
        p: [
          "En el perfil eliges tu avatar y el nombre que ven los demás. Comparte solo lo que quieras que sea público y nunca pongas datos sensibles.",
        ],
      },
      {
        h: "Modo Diseñar",
        p: [
          "En Modo Diseñar personalizas cómo se ve tu juego:",
        ],
        list: [
          "Tus bloques comunes y tu bloque especial (formas, colores y animaciones).",
          "El tablero: casillas claras, casillas oscuras, paredes y molduras.",
          "Diseños guardados en Mis tableros para cambiar cuando quieras.",
          "El botón «Por defecto» para volver al aspecto original.",
        ],
        after: [
          "Todo se guarda solo, sin pulsar ningún botón. Los diseños son solo estéticos: no cambian ninguna regla.",
        ],
      },
    ],
    faqs: [
      ["¿Dónde veo mi código de jugador?", "En tu perfil y en la sección Amigos."],
      ["¿Tengo que guardar los cambios en Modo Diseñar?", "No. Se guardan solos."],
    ],
    related: ["amigos", "tienda", "cuenta", "ajustes"],
    cta: { label: "Amigos", slug: "amigos" },
    legacy: [
      "perfil/codigo-de-jugador",
      "perfil/avatar",
      "tutoriales/editar-perfil",
      "perfil/estado-jugador",
      "perfil/amigo",
      "cuenta/codigo-de-jugador",
    ],
  },
  {
    slug: "amigos",
    theme: "comunidad",
    nav: "Amigos y retos",
    title: "Amigos en XIMA: retos, revanchas e invita y gana",
    description:
      "Cómo agregar amigos en XIMA con su código, retarlos con apuesta de monedas, pedir la revancha, hacer un duelo de puzzle o un mini torneo, y ganar monedas invitando.",
    h1: "Amigos y retos",
    intro:
      "XIMA es más divertida con amigos. En la sección Amigos los encuentras, los retas y ganas monedas por cada amigo que traes.",
    sections: [
      {
        h: "Agregar un amigo",
        list: [
          "Pídele su código de jugador (XIMA-…).",
          "Escríbelo o pégalo en el buscador de Amigos.",
          "Envíale una solicitud de amistad.",
          "Cuando la acepte, aparecerá en tu lista.",
        ],
        after: [
          "Desde la misma sección ves las solicitudes que te han enviado y las que tú enviaste, y puedes aceptarlas, rechazarlas o cancelarlas.",
        ],
      },
      {
        h: "Retos",
        list: [
          "Partida con apuesta: cada uno pone las mismas monedas y el ganador se las lleva.",
          "Revancha: vuelve a jugar contra el mismo rival; esta vez empieza el otro.",
          "Duelo de puzzle: los dos resuelven el mismo puzzle.",
          "Mini torneo: un pequeño torneo entre amigos.",
        ],
        after: [
          "Las partidas de los retos llevan contraseña para que solo entren los invitados. Las partidas por turnos, para jugar sin estar conectados a la vez, llegarán pronto.",
        ],
      },
      {
        h: "Invita y gana",
        p: [
          "Comparte tu enlace de invitación. Cuando un amigo se registra con él y juega su primera partida, recibes monedas, y hay un bono extra cuando llegas a cinco amigos invitados.",
        ],
      },
    ],
    faqs: [
      ["¿Cómo encuentro a un amigo en XIMA?", "Con su código de jugador, en el buscador de la sección Amigos."],
      ["¿Puedo jugar con alguien que no es mi amigo?", "Sí: comparte con él el enlace de una partida. Puede entrar incluso como invitado."],
      ["¿Qué pasa con las monedas de una apuesta?", "Se guardan al aceptar el reto y se las lleva el ganador de la partida."],
    ],
    related: ["perfil", "tutoriales/unirse-partida-privada", "chat-de-voz", "tienda"],
    cta: { label: "Tu perfil", slug: "perfil" },
    legacy: [
      "tutoriales/enviar-solicitud-amistad",
      "tutoriales/invitar-amigo-a-partida",
      "tutoriales/buscar-jugador",
      "tutoriales/aceptar-solicitud-amistad",
      "tutoriales/rechazar-solicitud-amistad",
      "tutoriales/cancelar-solicitud-amistad",
      "amigos/privacidad",
      "amigos/invitaciones",
      "amigos/jugar-con-amigo",
      "amigos/solicitudes",
      "solucion/no-veo-amigos",
    ],
  },
  {
    slug: "chat-de-voz",
    theme: "comunidad",
    nav: "Chat de voz",
    title: "Chat de voz en XIMA: hablar con tus rivales",
    description:
      "Cómo usar el chat de voz en las partidas de XIMA y en el Campeonato: activar el micrófono, dar permiso al navegador, silenciar y qué hacer si no funciona.",
    h1: "Chat de voz",
    intro:
      "En las partidas compatibles puedes hablar con los demás jugadores por micrófono, como si estuvierais en la misma mesa.",
    sections: [
      {
        h: "Cómo activarlo",
        list: [
          "Entra en la partida.",
          "Busca la sección Voz jugadores.",
          "Pulsa tu botón de micrófono.",
          "Si el navegador te pide permiso para usar el micrófono, acéptalo.",
          "Pulsa de nuevo para cerrar el micrófono cuando no quieras hablar.",
        ],
      },
      {
        h: "En el Campeonato",
        p: [
          "El Campeonato tiene su propia sala de voz y chat: hablan los inscritos y el organizador, y escucha todo el que esté viendo. Puedes silenciar a un jugador concreto o dejar de escuchar a todos.",
        ],
      },
      {
        h: "Si no se oye",
        list: [
          "Revisa que diste permiso al micrófono (normalmente, en el candado de la barra de direcciones).",
          "Comprueba que otro programa no está usando el micrófono.",
          "Recarga la página una vez.",
        ],
      },
      {
        h: "Con respeto",
        p: [
          "No compartas por voz contraseñas, datos de pago ni información personal, y trata a los demás como te gustaría que te trataran.",
        ],
      },
    ],
    faqs: [
      ["¿El chat de voz es lo mismo que el control por voz?", "No. El chat de voz sirve para hablar con otros jugadores; el control por voz sirve para dar órdenes al juego."],
    ],
    related: ["control-por-voz", "campeonato", "amigos"],
    cta: { label: "Control por voz", slug: "control-por-voz" },
  },
  {
    slug: "noticias",
    theme: "comunidad",
    nav: "Noticias y Comunidad",
    title: "Noticias y Comunidad de XIMA",
    description:
      "En Noticias verás todo lo nuevo de XIMA: mejoras, torneos, puzzles, el próximo campeonato y en qué estamos trabajando. En Comunidad, abre temas y conversa.",
    h1: "Noticias y Comunidad",
    intro:
      "Noticias es el sitio para estar al día de XIMA, y Comunidad es el foro donde los jugadores conversan.",
    sections: [
      {
        h: "Noticias",
        list: [
          "Novedades y mejoras de la web.",
          "Torneos y puzzles nuevos.",
          "Eventos y la cuenta atrás del próximo campeonato.",
          "En qué trabajamos: las próximas mejoras y su avance.",
          "Un calendario con lo que viene.",
        ],
        after: ["Pulsa Avisarme si quieres que el navegador te avise cuando haya algo nuevo."],
      },
      {
        h: "Comunidad",
        p: [
          "En Comunidad puedes abrir un tema (una duda, una partida que quieras comentar, una idea), responder a otros y dar me gusta. Si te equivocas, puedes editar lo que escribiste.",
        ],
      },
    ],
    faqs: [
      ["¿Puedo proponer mejoras para XIMA?", "Sí. Abre un tema en Comunidad o escríbenos desde Soporte."],
    ],
    related: ["campeonato", "torneos", "soporte"],
    cta: { label: "El Campeonato", slug: "campeonato" },
    legacy: ["novedades"],
  },
  {
    slug: "torneos",
    theme: "torneos",
    nav: "Torneos",
    title: "Torneos de XIMA: cómo unirte y competir",
    description:
      "Cómo funcionan los torneos de XIMA: torneos en vivo y por empezar, filtros por modo, jugadores y tiempo, contraseña, coste de entrada, premios y la sala de espera.",
    h1: "Torneos",
    intro:
      "En Todos los torneos encuentras las competiciones abiertas de XIMA: en vivo, por empezar y aquellas en las que ya estás inscrito.",
    sections: [
      {
        h: "La página de torneos",
        list: [
          "Arriba, cuántos torneos hay en vivo, por empezar y en cuántos estás inscrito.",
          "Tarjetas por modo con el número de torneos abiertos de cada uno.",
          "Filtros: Todos, En vivo o Por empezar, y también por número de jugadores y minutos por turno.",
          "Si ya estás inscrito en uno, aparece arriba con el botón Entrar.",
        ],
      },
      {
        h: "Cada torneo",
        p: [
          "Cada tarjeta muestra el número del torneo, el modo, los jugadores inscritos y el tiempo por turno, y cuándo empieza y termina. Algunos torneos tienen además contraseña, un coste de entrada en monedas y un premio. Revisa bien esos datos antes de unirte.",
        ],
      },
      {
        h: "La sala de espera",
        p: [
          "Al unirte entras en la sala de espera, donde ves a los demás jugadores, el chat de la sala y la cuenta atrás. Desde ahí puedes invitar a otros, salir de la sala si cambias de idea o empezar a jugar cuando llegue el momento.",
        ],
      },
      {
        h: "Consejos",
        list: [
          "Elige el modo y el tiempo en los que te sientes más cómodo.",
          "Calienta antes con una partida contra el bot.",
          "Usa el chat para coordinarte, sin compartir datos personales.",
        ],
      },
    ],
    faqs: [
      ["¿Los torneos cuestan algo?", "Algunos son gratis y otros tienen un coste de entrada en monedas del juego. Se indica en cada tarjeta."],
      ["¿Puedo salir de un torneo antes de empezar?", "Sí, desde la sala de espera con Salir de la sala."],
      ["¿Qué diferencia hay con el Campeonato?", "El Campeonato es la competición oficial por eliminación directa, con calendario y premio. Tiene su propia página."],
    ],
    related: ["campeonato", "partidas-online", "ranking", "tienda"],
    cta: { label: "El Campeonato", slug: "campeonato" },
    legacy: [
      "tutoriales/unirse-a-torneo",
      "tutoriales/crear-torneo",
      "torneos/sala-de-espera",
      "torneos/chat-sala",
      "torneos/invitar",
      "torneos/salir-sala",
      "torneos/jugar-desde-sala",
      "torneos/recompensas",
      "torneos/costo",
      "torneos/posiciones",
      "torneos/ganador",
      "torneos/activos",
      "torneos/futuros",
      "torneos/como-funcionan",
      "torneos/seguir-sala",
      "torneos/revisar-recompensas",
    ],
  },
  {
    slug: "campeonato",
    theme: "torneos",
    nav: "El Campeonato",
    title: "Campeonato de XIMA: eliminación directa hasta la final",
    description:
      "Cómo funciona el Campeonato de XIMA: inscripción, cuadro de eliminación directa con octavos, cuartos, semifinales y final, horarios, bots de relleno, voz y premios.",
    h1: "El Campeonato",
    intro:
      "El Campeonato es la competición oficial de XIMA: un cuadro de eliminación directa en forma de pirámide, del que solo sale un campeón.",
    sections: [
      {
        h: "Cómo funciona",
        list: [
          "Te inscribes en la página del Campeonato mientras la inscripción esté abierta (algunos campeonatos llevan contraseña).",
          "Se juega por eliminación directa: octavos de final, cuartos, semifinales y la gran final. Quien gana, pasa de ronda.",
          "Si faltan jugadores para completar el cuadro, las plazas libres las ocupa el Bot XIMA. Nunca se juega bot contra bot.",
          "Cada partido tiene su lugar en el cuadro y, si el organizador la fija, su hora en el calendario.",
        ],
      },
      {
        h: "Horarios y puntualidad",
        p: [
          "Los partidos pueden empezar todos a la vez, de forma escalonada o a la hora que el organizador fije para cada uno. Si un partido no tiene hora, empieza en cuanto entran los dos jugadores.",
          "Si llegas tarde, el retraso se descuenta de tu reloj. Si no te presentas, gana tu rival; si no se presenta ninguno, el siguiente rival pasa directamente.",
        ],
      },
      {
        h: "Ver el Campeonato",
        p: [
          "Cualquiera puede seguir el Campeonato: los partidos en juego aparecen marcados EN VIVO y con un botón para verlos; los terminados, con su repetición. La sala del Campeonato tiene chat y voz: hablan los jugadores y el organizador, y escucha todo el mundo.",
        ],
      },
      {
        h: "Premio y archivo",
        p: [
          "Cada campeonato indica su premio, si lo tiene. Al terminar, el campeonato queda guardado en el archivo y cuenta en las estadísticas de torneos ganados del campeón.",
        ],
      },
    ],
    faqs: [
      ["¿Cuántos jugadores tiene el Campeonato?", "Hasta 16, en un cuadro de octavos, cuartos, semifinales y final. Las plazas vacías las ocupa el Bot XIMA."],
      ["¿Qué pasa si no me presento a mi partido?", "Gana tu rival y pasa a la siguiente ronda."],
    ],
    related: ["torneos", "chat-de-voz", "noticias", "ranking"],
    cta: { label: "Todos los torneos", slug: "torneos" },
  },
  {
    slug: "puzzles",
    theme: "puzzles",
    nav: "Puzzles",
    title: "Puzzles de XIMA: un reto nuevo cada día",
    description:
      "Los puzzles de XIMA son posiciones en las que tienes que llevar tu especial a la cima en pocos turnos. Un puzzle gratis al día, recompensas, rachas y consejos.",
    h1: "Puzzles",
    intro:
      "Un puzzle de XIMA es una posición ya preparada con un objetivo claro: llevar tu bloque especial a la cima (G7) antes de que se acaben los turnos. Es el mejor entrenamiento para los finales.",
    sections: [
      {
        h: "Cómo se juega un puzzle",
        list: [
          "En la página de Puzzles, cada tarjeta muestra una vista previa en 2D del tablero, el modo y la recompensa.",
          "Ábrelo y se juega en el tablero 3D.",
          "Lee el objetivo y el límite de turnos.",
          "Busca la secuencia que lleva tu especial a la cima.",
          "Si lo resuelves, cobras la recompensa; si fallas, puedes reintentarlo o pasar al siguiente.",
        ],
      },
      {
        h: "Un puzzle gratis cada día",
        p: [
          "Sin Premium puedes abrir un puzzle nuevo al día; el cupo se renueva a medianoche. Con Premium resuelves todos los que quieras. Para cobrar las recompensas tienes que estar registrado.",
        ],
      },
      {
        h: "Tus estadísticas de puzzles",
        list: [
          "Puzzles resueltos de los que has jugado.",
          "Tu racha: los días seguidos que has resuelto al menos uno.",
          "Tu mejor tiempo en cada puzzle.",
          "Los bonos que ya has cobrado.",
        ],
      },
      {
        h: "Cómo resolverlos",
        list: [
          "Empieza por el final: ¿desde qué casillas puede tu especial llegar a G7?",
          "Busca el escalón: casi siempre hace falta un bloque que apoye la última subida.",
          "Recuerda que un común puede pasar por encima de G7 y que tu especial no puede pasar sobre el contrario.",
          "Si el especial está capturado, piensa en el rescate.",
          "Usa los filtros (más recientes, mayor recompensa, sin resolver) para encontrar el siguiente.",
        ],
      },
    ],
    faqs: [
      ["¿Los puzzles son gratis?", "Tienes un puzzle nuevo gratis cada día. Con Premium, todos los que quieras."],
      ["¿Qué pasa si fallo un puzzle?", "Puedes volver a intentarlo o pasar a otro."],
    ],
    related: ["estrategia/finales-xima", "reglas/cima", "premium", "bots"],
    cta: { label: "Finales en XIMA", slug: "estrategia/finales-xima" },
    legacy: [
      "tutoriales/resolver-puzzle",
      "tutoriales/abrir-puzzle",
      "tutoriales/reintentar-puzzle",
      "tutoriales/siguiente-puzzle",
      "puzzles/modos",
      "puzzles/recompensas",
      "puzzles/tiempo",
      "puzzles/completado",
      "puzzles/fallido",
      "puzzles/como-entrenar",
      "puzzles/leer-objetivo",
      "puzzles/usar-recompensas",
      "solucion/puzzle-no-avanza",
    ],
  },
  {
    slug: "bots",
    theme: "puzzles",
    nav: "Jugar contra el bot",
    title: "Jugar contra el bot de XIMA: niveles y consejos",
    description:
      "Practica XIMA contra la inteligencia artificial: elige modo, de 1 a 3 bots rivales, tiempo por turno y dificultad Fácil, Medio, Difícil o Injusto.",
    h1: "Jugar contra el bot",
    intro:
      "El bot de XIMA es el mejor compañero de entrenamiento: siempre está disponible, no se cansa y puedes elegir lo fuerte que juega.",
    sections: [
      {
        h: "Cómo empezar",
        list: [
          "Abre Jugar con bot.",
          "Elige el modo: Ciencia, Azar, Cienzar o Ciencia V/S Azar.",
          "Elige cuántos bots rivales quieres: 1 contra 1, 1 contra 2 o 1 contra 3.",
          "Elige el tiempo por turno.",
          "Elige la dificultad y pulsa Jugar.",
        ],
      },
      {
        h: "Los cuatro niveles",
        table: {
          head: ["Nivel", "Para quién"],
          rows: [
            ["Fácil", "Para practicar y aprender las reglas."],
            ["Medio", "Un desafío equilibrado."],
            ["Difícil", "Solo para expertos."],
            ["Injusto", "Máxima presión: el bot más fuerte."],
          ],
        },
      },
      {
        h: "Cómo piensa el bot",
        p: [
          "El bot calcula sus jugadas con un motor que evalúa miles de posiciones y que se entrena con partidas de XIMA. En Ciencia V/S Azar juega ciencia o azar según el rol que le toque, y en los modos con dados tiene en cuenta los números que salen.",
          "Ese mismo motor es el que analiza tus partidas en Analizar partida y el que mueve la barra de evaluación durante las partidas de dos jugadores.",
        ],
      },
      {
        h: "Cómo entrenar",
        list: [
          "Empieza en Fácil y sube de nivel cuando ganes varias partidas seguidas.",
          "Repite una misma apertura varias veces para ver cómo responde.",
          "Después de cada derrota, analiza la partida y busca tu error grave.",
          "Juega 1 contra 2 o 1 contra 3 para aprender a vigilar a varios rivales.",
        ],
      },
      {
        h: "Si la partida no empieza",
        list: [
          "Comprueba tu conexión y recarga la página.",
          "Espera a que el tablero termine de cargar antes de tocar nada.",
          "Si sigue sin empezar, escríbenos desde Soporte.",
        ],
      },
    ],
    faqs: [
      ["¿Contra el bot cuenta para el ranking?", "Las partidas se guardan en tu historial, para que puedas repasarlas y analizarlas."],
      ["¿Cuál es el nivel más difícil?", "Injusto."],
      ["¿Puedo jugar contra varios bots?", "Sí, hasta tres bots a la vez."],
    ],
    related: ["analizar-partida", "ciencia", "academia", "modos/freebox"],
    cta: { label: "Analizar una partida", slug: "analizar-partida" },
    legacy: [
      "tutoriales/jugar-con-bot",
      "bots/modo-libre",
      "bots/azar",
      "bots/dificultad",
      "bots/dificultad-facil",
      "bots/dificultad-media",
      "bots/dificultad-dificil",
      "bots/elegir-bot",
      "ciencia/entrenar-ia",
      "solucion/bot-no-inicia",
      "articulos/inteligencia-artificial-juegos-de-mesa",
    ],
  },
];
